import { Check, Copy, Edit2, Eye, EyeOff, Globe, KeyRound, LogIn, Plus, Power, Trash2 } from 'lucide-react';
import ENTITY_ICONS from '../components/common/entityIcons';
import React, { useCallback, useMemo, useState } from 'react';
import DataTable, { CopyableId, RowAction, RowActions, RowLink } from '../components/common/DataTable';
import StatusBadge from '../components/common/StatusBadge';
import EmailProviderSettings from '../components/EmailProviderSettings';
import Modal from '../components/common/Modal';
import PageHeader from '../components/common/PageHeader';
import { NewUserForm } from '../components/NewUserForm';
import { Typeahead } from '../components/ui/Typeahead';
import { Switch } from '../components/ui/Switch';
import Tooltip from '../components/ui/Tooltip';
import { Segmented } from '../components/ui/Segmented';
import { NumberInput, TextInput } from '../components/ui/Input';
import { SettingField, SettingRow, SettingsGrid, SettingsSection } from '../components/common/Settings';
import { useApps } from '../contexts/AppsContext';
import { useNotice } from '../components/common/Notices';
import { useConfirm } from '../components/common/ConfirmDialog';
import useServerTable from '../hooks/useServerTable';
import { STATUS_OPTIONS, formatDate } from '../components/common/filters';
import appsService from '../services/apps';
import oauthConfigService from '../services/oauthConfig';
import useAppUsers, { ALL_APPS } from '../hooks/useAppUsers';
import {
    CLAIM_SOURCES,
    TENANT_ID_TRANSFORMS,
    defaultClaimProfile,
    generateUuid,
    previewClaimNames,
    validateClaimProfile,
} from '../utils/claimProfile';
import { isValidWebhookUrl } from '../utils/webhookUrl';
import { getIsPortalAdmin } from '../utils/storage';

const FEATURE_FLAGS = [
    {
        key: 'session_idle_timeout_minutes',
        label: 'Session idle timeout (min)',
        type: 'number',
        placeholder: '15',
        hint: 'Minutes of inactivity before session expires',
    },
    { key: 'audit_trail_enabled', label: 'Audit trail', type: 'toggle', hint: 'Structured audit logging' },
    {
        key: 'audit_retention_years',
        label: 'Audit retention (years)',
        type: 'number',
        placeholder: '7',
        hint: 'Years to retain audit logs',
    },
    {
        key: 'password_history_count',
        label: 'Password history',
        type: 'number',
        placeholder: '5',
        hint: 'Number of previous passwords to remember',
    },
    {
        key: 'enforce_password_expiry',
        label: 'Enforce password expiry',
        type: 'toggle',
        hint: 'Force reset when max_age_days exceeded',
    },
    {
        key: 'delegated_access_enabled',
        label: 'Delegated access',
        type: 'toggle',
        hint: 'Allow users to grant proxy access',
    },
    { key: 'user_groups_enabled', label: 'User groups', type: 'toggle', hint: 'Team and group management' },
    { key: 'webhooks_enabled', label: 'Webhooks', type: 'toggle', hint: 'Event notifications to external URLs' },
    {
        key: 'totp_mfa_enabled',
        label: 'Authenticator app (TOTP)',
        type: 'toggle',
        hint: 'Enrolled users enter an authenticator code instead of a one-time code (applies even when 2FA is off)',
    },
    { key: 'bulk_operations_enabled', label: 'Bulk operations', type: 'toggle', hint: 'Bulk invitations' },
    { key: 'sso_enabled', label: 'Enterprise SSO', type: 'toggle', hint: 'SAML/OIDC federation' },
    { key: 'scope_rbac_enabled', label: 'Scope RBAC', type: 'toggle', hint: 'Own/team/all data filtering' },
    {
        key: 'org_hierarchy_enabled',
        label: 'Org hierarchy',
        type: 'toggle',
        hint: 'Org-unit tree with permission inheritance',
    },
    {
        key: 'external_users_enabled',
        label: 'External users',
        type: 'toggle',
        hint: 'Provider-portal users scoped to an owning organisation',
    },
    {
        key: 'slim_access_token',
        label: 'Slim access token',
        type: 'toggle',
        hint: 'Skip component_details embed (roles-based RBAC apps, avoids cookie size limit)',
    },
    {
        key: 'max_active_users',
        label: 'Active user limit',
        type: 'number',
        placeholder: 'Unlimited',
        hint: 'Max active seats (enabled members + pending invites). Blank = unlimited',
    },
];

// OTP delivery channels selectable per app (tenant override handled on the Tenants page).
const MFA_CHANNEL_OPTIONS = [
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'whatsapp', label: 'WhatsApp' },
];

// JWT signing-key types managed by the portal-admin-only Signing Keys panel.
// Presence-only (never a secret value) — see docs for
// GET/POST /api/application/signing-keys*.
const SIGNING_KEY_TYPES = [
    { type: 'AUTHORISE_JWT_KEY', label: 'Access token' },
    { type: 'REFRESH_JWT_KEY', label: 'Refresh token' },
    { type: 'AUTHENTICATE_JWT_KEY', label: 'Auth (pre-2FA) token' },
    { type: 'TEMPORARY_JWT_KEY', label: 'Password-reset token' },
];

const TENANT_ID_PREFIX_PATTERN = /^[A-Z]{2,6}$/;

const INPUT_CLASS =
    'w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 placeholder:text-slate-400 transition-shadow focus:outline-none focus:ring-2 focus:ring-primary-200 focus:border-primary-300';

const parseConfig = (str) => {
    try {
        return JSON.parse(str || '{}');
    } catch {
        return {};
    }
};

// Token signing + claim shaping. Everything here writes into app_config:
// jwt_signing_algorithm / jwks_enabled / jwt_iss / jwt_aud, plus the declarative
// `claim_profile` consumed by services/common.utils.js:normalizeClaimProfile.
// Without this panel a claim profile could only be authored by hand-editing the
// raw App Config JSON.
const TokenClaimsPanel = ({ config, updateConfig, children }) => {
    const isRs256 = String(config.jwt_signing_algorithm || '').toUpperCase() === 'RS256';
    const profile = config.claim_profile && typeof config.claim_profile === 'object' ? config.claim_profile : null;
    const errors = useMemo(() => validateClaimProfile(profile), [profile]);
    const previewNames = useMemo(() => previewClaimNames(profile), [profile]);

    const setKey = (key, value) => {
        const updated = { ...config };
        if (value === '' || value === null || value === undefined || value === false) {
            delete updated[key];
        } else {
            updated[key] = value;
        }
        updateConfig(updated);
    };

    const setProfile = (patch) => updateConfig({ ...config, claim_profile: { ...profile, ...patch } });

    const toggleRemapping = () => {
        if (profile) {
            const updated = { ...config };
            delete updated.claim_profile;
            updateConfig(updated);
        } else {
            updateConfig({ ...config, claim_profile: defaultClaimProfile() });
        }
    };

    const setClaimName = (source, value) => {
        const map = { ...(profile.claim_map || {}) };
        if (value.trim()) {
            map[source] = value.trim();
        } else {
            delete map[source];
        }
        setProfile({ claim_map: map });
    };

    // `tenant_id` must stay retained whenever it is remapped or transformed, or
    // UM's own auth middleware can no longer resolve the user's role mapping.
    const setRetainTenantId = (on) => {
        const list = (profile.retain_claims || []).filter((c) => c !== 'tenant_id');
        setProfile({ retain_claims: on ? [...list, 'tenant_id'] : list });
    };

    const setTransform = (type) => {
        if (type === 'none') {
            setProfile({ tenant_id_transform: { type: 'none' } });
            return;
        }
        const existing = profile.tenant_id_transform || {};
        setProfile({ tenant_id_transform: { type, namespace: existing.namespace || generateUuid() } });
    };

    const setPlatformAdmin = (on) => {
        if (!on) {
            const next = { ...profile };
            delete next.platform_admin;
            updateConfig({ ...config, claim_profile: next });
            return;
        }
        setProfile({
            platform_admin: { when_role: 'Admin', grant_role: 'Super Admin', sentinel_tenant: 'platform' },
        });
    };

    const transformType = String(
        (profile && profile.tenant_id_transform && profile.tenant_id_transform.type) || 'none'
    );
    const retainsTenantId = Boolean(profile && (profile.retain_claims || []).includes('tenant_id'));

    return (
        <div className="space-y-6">
            <SettingsGrid>
                {children}
                <SettingField
                    label="Signing algorithm"
                    hint={isRs256 ? 'Per-app RSA key.' : 'Shared secret (default).'}
                >
                    <Segmented
                        options={[
                            { value: 'HS256', label: 'HS256' },
                            { value: 'RS256', label: 'RS256' },
                        ]}
                        value={isRs256 ? 'RS256' : 'HS256'}
                        onChange={(v) => setKey('jwt_signing_algorithm', v === 'RS256' ? 'RS256' : '')}
                    />
                </SettingField>
                <SettingRow
                    label="Publish JWKS"
                    hint="Expose the public key for verification. RS256 only."
                    muted={!isRs256}
                >
                    <Switch
                        checked={!!config.jwks_enabled && isRs256}
                        disabled={!isRs256}
                        onChange={(on) => setKey('jwks_enabled', on)}
                        label="Publish JWKS"
                    />
                </SettingRow>
                <SettingField label="Issuer (iss)">
                    <TextInput
                        type="text"
                        value={config.jwt_iss || ''}
                        onChange={(e) => setKey('jwt_iss', e.target.value)}
                        placeholder="Omitted when blank"
                        className="font-mono"
                    />
                </SettingField>
                <SettingField label="Audience (aud)">
                    <TextInput
                        type="text"
                        value={config.jwt_aud || ''}
                        onChange={(e) => setKey('jwt_aud', e.target.value)}
                        placeholder="Omitted when blank"
                        className="font-mono"
                    />
                </SettingField>
                <SettingRow
                    label="Remap identity claims"
                    hint="Emit UM's identity values under names this app's consumers expect. Off keeps UM's standard claim shape."
                >
                    <Switch checked={!!profile} onChange={toggleRemapping} label="Remap identity claims" />
                </SettingRow>
            </SettingsGrid>

            {profile && (
                <div className="space-y-5 rounded-xl bg-slate-50 p-5">
                    <div>
                        <span className="block text-xs text-slate-500 mb-1">Profile name (label only)</span>
                        <TextInput
                            type="text"
                            value={profile.name || ''}
                            onChange={(e) => setProfile({ name: e.target.value })}
                            placeholder="mapped"
                            className="font-mono"
                        />
                    </div>

                    <div className="space-y-2">
                        <span className="block text-xs text-slate-500">
                            Claim names — leave blank to omit a value from the token
                        </span>
                        {CLAIM_SOURCES.map((source) => (
                            <div key={source.key} className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-start">
                                <div>
                                    <span className="text-sm text-slate-700">{source.label}</span>
                                    <p className="text-xs text-slate-400">{source.hint}</p>
                                </div>
                                <TextInput
                                    type="text"
                                    value={(profile.claim_map || {})[source.key] || ''}
                                    onChange={(e) => setClaimName(source.key, e.target.value)}
                                    placeholder={source.suggestion}
                                    className="font-mono"
                                />
                            </div>
                        ))}
                    </div>

                    <SettingRow label="Roles as names" hint="Emit role name strings instead of UM's role objects.">
                        <Switch
                            checked={!!profile.roles_as_names}
                            onChange={() => setProfile({ roles_as_names: !profile.roles_as_names })}
                            label="Roles as names"
                        />
                    </SettingRow>

                    <div>
                        <span className="block text-sm font-medium text-slate-800 mb-1.5">Tenant ID transform</span>
                        <Segmented
                            options={TENANT_ID_TRANSFORMS.map((t) => ({
                                value: t.value,
                                label: t.label.split(' — ')[0],
                            }))}
                            value={transformType}
                            onChange={setTransform}
                        />
                        <p className="mt-1.5 text-xs text-slate-500">
                            {(TENANT_ID_TRANSFORMS.find((t) => t.value === transformType) || {}).label}
                        </p>
                        {transformType === 'uuidv5' && (
                            <div className="mt-2">
                                <span className="block text-xs text-slate-500 mb-1">Namespace UUID</span>
                                <div className="flex gap-2">
                                    <TextInput
                                        type="text"
                                        value={(profile.tenant_id_transform || {}).namespace || ''}
                                        onChange={(e) =>
                                            setProfile({
                                                tenant_id_transform: { type: 'uuidv5', namespace: e.target.value },
                                            })
                                        }
                                        className="font-mono"
                                    />
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setProfile({
                                                tenant_id_transform: { type: 'uuidv5', namespace: generateUuid() },
                                            })
                                        }
                                        className="shrink-0 px-3 py-1.5 rounded text-sm border border-slate-200 text-slate-600 hover:bg-slate-50"
                                    >
                                        Generate
                                    </button>
                                </div>
                                <p className="text-xs text-amber-600 mt-1">
                                    Changing this changes every derived tenant id. Any consumer that mirrors the
                                    derivation must use the same value.
                                </p>
                            </div>
                        )}
                    </div>

                    <SettingRow
                        label="Also keep the original tenant_id"
                        hint="Required when tenant_id is remapped or transformed — UM resolves role mappings by the native tenant id."
                    >
                        <Switch
                            checked={!!retainsTenantId}
                            onChange={() => setRetainTenantId(!retainsTenantId)}
                            label="Also keep the original tenant_id"
                        />
                    </SettingRow>

                    <div>
                        <span className="block text-xs text-slate-500 mb-1">
                            Drop claims (comma-separated legacy claim names)
                        </span>
                        <TextInput
                            type="text"
                            value={(profile.drop_claims || []).join(', ')}
                            onChange={(e) =>
                                setProfile({
                                    drop_claims: e.target.value
                                        .split(',')
                                        .map((s) => s.trim())
                                        .filter(Boolean),
                                })
                            }
                            placeholder="tenant_name"
                            className="font-mono"
                        />
                    </div>

                    <SettingRow
                        label="Platform-admin binding"
                        hint="Give the app-level admin (no tenant) an extra role and bind it to a reserved pseudo-tenant, for consumers that require every request to name a tenant."
                    >
                        <Switch
                            checked={!!profile.platform_admin}
                            onChange={() => setPlatformAdmin(!profile.platform_admin)}
                            label="Platform-admin binding"
                        />
                    </SettingRow>

                    {profile.platform_admin && (
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                            {[
                                { key: 'when_role', label: 'When holding role', placeholder: 'Admin' },
                                { key: 'grant_role', label: 'Also grant role', placeholder: 'Super Admin' },
                                { key: 'sentinel_tenant', label: 'Pseudo-tenant', placeholder: 'platform' },
                            ].map((f) => (
                                <div key={f.key}>
                                    <span className="block text-xs text-slate-500 mb-1">{f.label}</span>
                                    <TextInput
                                        type="text"
                                        value={profile.platform_admin[f.key] || ''}
                                        onChange={(e) =>
                                            setProfile({
                                                platform_admin: {
                                                    ...profile.platform_admin,
                                                    [f.key]: e.target.value,
                                                },
                                            })
                                        }
                                        placeholder={f.placeholder}
                                        className="font-mono"
                                    />
                                </div>
                            ))}
                        </div>
                    )}

                    {previewNames.length > 0 && (
                        <div className="rounded-lg bg-slate-50 border border-slate-100 p-3">
                            <span className="block text-xs text-slate-500 mb-1">Identity claims in the token</span>
                            <div className="flex flex-wrap gap-1.5">
                                {previewNames.map((n) => (
                                    <span
                                        key={n}
                                        className="px-2 py-0.5 rounded bg-white border border-slate-200 text-xs font-mono text-slate-700"
                                    >
                                        {n}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {errors.length > 0 && (
                        <div className="rounded-lg bg-rose-50 border border-rose-200 p-3">
                            <span className="block text-xs font-medium text-rose-700 mb-1">Fix before saving</span>
                            <ul className="list-disc list-inside space-y-0.5">
                                {errors.map((e) => (
                                    <li key={e} className="text-xs text-rose-700">
                                        {e}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const FLAG_BY_KEY = Object.fromEntries(FEATURE_FLAGS.map((f) => [f.key, f]));

// Webhook endpoints stored inline in app_config.webhooks (shown when webhooks_enabled is on).
const WebhookEndpointsPanel = ({ config, updateConfig }) => {
    const webhooks = config.webhooks || [];
    const [newWebhookUrl, setNewWebhookUrl] = React.useState('');
    const [newWebhookEvents, setNewWebhookEvents] = React.useState('*');
    const [newWebhookDesc, setNewWebhookDesc] = React.useState('');

    const addWebhook = () => {
        if (!isValidWebhookUrl(newWebhookUrl)) return;
        const events = newWebhookEvents
            .split(',')
            .map((e) => e.trim())
            .filter(Boolean);
        updateConfig({
            ...config,
            webhooks: [
                ...webhooks,
                {
                    url: newWebhookUrl,
                    events: events.length ? events : ['*'],
                    description: newWebhookDesc || null,
                    active: true,
                },
            ],
        });
        setNewWebhookUrl('');
        setNewWebhookEvents('*');
        setNewWebhookDesc('');
    };

    const removeWebhook = (index) => updateConfig({ ...config, webhooks: webhooks.filter((_, i) => i !== index) });

    const toggleWebhookActive = (index) =>
        updateConfig({
            ...config,
            webhooks: webhooks.map((w, i) => (i === index ? { ...w, active: !w.active } : w)),
        });

    return (
        <div className="mt-6 rounded-xl bg-slate-50 p-4">
            <div className="text-sm font-medium text-slate-800">Webhook endpoints</div>
            <p className="mt-0.5 mb-3 text-xs text-slate-500">
                Consuming apps receive POST notifications when users, roles, components, groups, or delegations change.
            </p>

            {webhooks.length > 0 && (
                <div className="space-y-2 mb-3">
                    {webhooks.map((w, i) => (
                        <div
                            key={i}
                            className={`flex items-start gap-3 rounded-lg border bg-white p-2.5 text-sm ${
                                w.active ? 'border-slate-200' : 'border-slate-100 opacity-60'
                            }`}
                        >
                            <div className="flex-1 min-w-0">
                                <div className="font-mono text-xs text-slate-700 truncate">{w.url}</div>
                                <div className="flex flex-wrap gap-1 mt-1">
                                    {(w.events || ['*']).map((e, ei) => (
                                        <span
                                            key={ei}
                                            className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-500"
                                        >
                                            {e}
                                        </span>
                                    ))}
                                </div>
                                {w.description && <div className="text-xs text-slate-400 mt-0.5">{w.description}</div>}
                            </div>
                            <Switch checked={w.active} onChange={() => toggleWebhookActive(i)} label="Webhook active" />
                            <Tooltip label="Remove webhook">
                                <button
                                    type="button"
                                    onClick={() => removeWebhook(i)}
                                    className="px-1 text-xs text-red-400 hover:text-red-600"
                                    aria-label="Remove webhook"
                                >
                                    &times;
                                </button>
                            </Tooltip>
                        </div>
                    ))}
                </div>
            )}

            <div className="grid grid-cols-1 gap-2 lg:grid-cols-[2fr_1fr_1fr_auto]">
                <TextInput
                    type="url"
                    value={newWebhookUrl}
                    onChange={(e) => setNewWebhookUrl(e.target.value)}
                    placeholder="https://api.yourapp.com/hooks/user-mgmt"
                    className="font-mono"
                />
                <TextInput
                    value={newWebhookEvents}
                    onChange={(e) => setNewWebhookEvents(e.target.value)}
                    placeholder="* or user.*,role.*"
                />
                <TextInput
                    value={newWebhookDesc}
                    onChange={(e) => setNewWebhookDesc(e.target.value)}
                    placeholder="Description (optional)"
                />
                <button
                    type="button"
                    onClick={addWebhook}
                    disabled={!isValidWebhookUrl(newWebhookUrl)}
                    className="rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-40"
                >
                    Add
                </button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
                Events: <code className="bg-white px-1 rounded">user.*</code>{' '}
                <code className="bg-white px-1 rounded">role.*</code>{' '}
                <code className="bg-white px-1 rounded">component.*</code>{' '}
                <code className="bg-white px-1 rounded">group.*</code>{' '}
                <code className="bg-white px-1 rounded">delegation.*</code>{' '}
                <code className="bg-white px-1 rounded">*</code> (all)
            </p>
        </div>
    );
};

const SETTINGS_SECTIONS = [
    { id: 'app-general', label: 'General' },
    { id: 'app-signin', label: 'Sign-in & security' },
    { id: 'app-delivery', label: 'Email & SMS' },
    { id: 'app-sessions', label: 'Sessions' },
    { id: 'app-access', label: 'Access control' },
    { id: 'app-audit', label: 'Audit & integrations' },
    { id: 'app-tokens', label: 'Tokens & claims' },
    { id: 'app-advanced', label: 'Advanced' },
];

// App settings shared by the Create (step 1) and Update modals. Every field sits in the
// section that matches what it governs; DB columns (two_factor_auth, parallel_login, ...)
// and app_config keys are mixed freely because admins think in features, not storage.
const AppSettingsFields = ({ formData, setFormData, tenantIdPrefixValid, isCreate, appId }) => {
    const set = (key) => (value) => setFormData({ ...formData, [key]: value });
    const setBit = (key) => (on) => setFormData({ ...formData, [key]: on ? 1 : 0 });

    const config = parseConfig(formData.app_config);
    const updateConfig = (updated) => setFormData({ ...formData, app_config: JSON.stringify(updated, null, 2) });
    const toggleFlag = (key) => (on) => updateConfig({ ...config, [key]: on });
    const setNumberFlag = (key) => (value) => {
        const updated = { ...config };
        if (value === '' || value === null) {
            delete updated[key];
        } else {
            updated[key] = Number(value);
        }
        updateConfig(updated);
    };

    // OTP delivery channels (config.mfa_channels array + config.mfa_default_channel).
    const mfaChannels = Array.isArray(config.mfa_channels) ? config.mfa_channels : [];
    const twoFactorOn = formData.two_factor_auth === 1;
    const toggleMfaChannel = (value) => {
        const next = mfaChannels.includes(value) ? mfaChannels.filter((c) => c !== value) : [...mfaChannels, value];
        const updated = { ...config };
        if (next.length) {
            updated.mfa_channels = next;
        } else {
            delete updated.mfa_channels;
        }
        // Keep the default channel valid against the new selection.
        if (updated.mfa_default_channel && !next.includes(updated.mfa_default_channel)) {
            delete updated.mfa_default_channel;
        }
        updateConfig(updated);
    };

    // Active-user seat usage (read-only; fetched for the app being edited).
    const [seatUsage, setSeatUsage] = React.useState(null);
    React.useEffect(() => {
        let active = true;
        if (appId) {
            appsService
                .getSeatUsage(appId)
                .then((u) => {
                    if (active) setSeatUsage(u);
                })
                .catch(() => {
                    /* non-fatal: badge simply hides */
                });
        }
        return () => {
            active = false;
        };
    }, [appId]);

    const flagRow = (key, extra = {}) => {
        const flag = FLAG_BY_KEY[key];
        return (
            <SettingRow key={key} label={flag.label} hint={flag.hint} muted={extra.muted}>
                {flag.type === 'toggle' ? (
                    <Switch checked={!!config[key]} onChange={toggleFlag(key)} label={flag.label} />
                ) : (
                    <NumberInput value={config[key]} onChange={setNumberFlag(key)} placeholder={flag.placeholder} />
                )}
            </SettingRow>
        );
    };

    const [activeSection, setActiveSection] = React.useState(SETTINGS_SECTIONS[0].id);
    // Follow the scroll position so the section nav always marks what is on screen.
    React.useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                const visible = entries.find((e) => e.isIntersecting);
                if (visible) setActiveSection(visible.target.id);
            },
            { rootMargin: '0px 0px -75% 0px' }
        );
        SETTINGS_SECTIONS.forEach((sec) => {
            const el = document.getElementById(sec.id);
            if (el) observer.observe(el);
        });
        return () => observer.disconnect();
    }, []);
    const jumpTo = (id) => {
        setActiveSection(id);
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[176px_minmax(0,1fr)]">
            <nav className="hidden lg:block" aria-label="Settings sections">
                <ul className="sticky top-0 space-y-0.5">
                    {SETTINGS_SECTIONS.map((s) => (
                        <li key={s.id}>
                            <button
                                type="button"
                                onClick={() => jumpTo(s.id)}
                                className={`w-full rounded-md px-3 py-1.5 text-left text-sm transition-colors ${
                                    activeSection === s.id
                                        ? 'bg-primary-50 font-medium text-primary-700'
                                        : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                            >
                                {s.label}
                            </button>
                        </li>
                    ))}
                </ul>
            </nav>

            <div className="space-y-8">
                <SettingsSection id="app-general" title="General">
                    <SettingsGrid>
                        <SettingField label={isCreate ? 'App name *' : 'App name'}>
                            <TextInput
                                type="text"
                                required={isCreate}
                                value={formData.app_name}
                                onChange={(e) => set('app_name')(e.target.value)}
                                placeholder="My Application"
                            />
                        </SettingField>
                        <SettingField
                            className="md:col-span-1 xl:col-span-2"
                            label="Allowed origins"
                            hint="Comma-separated CORS origins. Empty uses the default policy."
                        >
                            <TextInput
                                type="text"
                                value={formData.allowed_origins}
                                onChange={(e) => set('allowed_origins')(e.target.value)}
                                placeholder="http://localhost:3000, https://myapp.example.com"
                            />
                        </SettingField>
                        <div className="space-y-3">
                            <SettingRow
                                label="Multi-tenant"
                                hint={
                                    isCreate
                                        ? 'Users belong to one tenant per app. Create tenants later on the Tenants page.'
                                        : 'Users belong to one tenant per app.'
                                }
                            >
                                <Switch
                                    checked={formData.is_multi_tenant === 1}
                                    onChange={setBit('is_multi_tenant')}
                                    label="Multi-tenant"
                                />
                            </SettingRow>
                            {formData.is_multi_tenant === 1 && (
                                <div>
                                    <label className="block text-xs font-medium text-slate-600 mb-1">
                                        Tenant ID prefix
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.tenant_id_prefix}
                                        onChange={(e) => set('tenant_id_prefix')(e.target.value)}
                                        className={`w-32 rounded-lg border px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 ${
                                            tenantIdPrefixValid
                                                ? 'border-slate-200 focus:ring-primary-200'
                                                : 'border-red-300 focus:ring-red-200'
                                        }`}
                                        placeholder="INF"
                                    />
                                    {!tenantIdPrefixValid && (
                                        <p className="text-xs text-red-600 mt-1">
                                            Must be 2-6 uppercase letters (A-Z), e.g. INF
                                        </p>
                                    )}
                                </div>
                            )}
                        </div>
                        <SettingRow
                            label="Emails"
                            hint="Send invites, one-time codes and password resets from User Management."
                        >
                            <Switch
                                checked={formData.enable_emails === 1}
                                onChange={setBit('enable_emails')}
                                label="Emails"
                            />
                        </SettingRow>
                        <div className="space-y-2">
                            {flagRow('max_active_users')}
                            {seatUsage && (
                                <p className="text-xs text-slate-500">
                                    In use:{' '}
                                    <span className="font-medium tabular-nums text-slate-700">
                                        {seatUsage.used} / {seatUsage.limit === null ? '∞' : seatUsage.limit}
                                    </span>
                                    {seatUsage.limit !== null && seatUsage.used >= seatUsage.limit && (
                                        <span className="ml-2 text-amber-600">limit reached</span>
                                    )}
                                </p>
                            )}
                        </div>
                    </SettingsGrid>
                </SettingsSection>

                <SettingsSection
                    id="app-signin"
                    title="Sign-in & security"
                    hint="Two-factor authentication (2FA) adds a second step after the password. UM console logins always require it."
                >
                    <SettingsGrid>
                        <SettingRow
                            label="Two-factor authentication (2FA)"
                            hint="Send a one-time code after a correct password."
                        >
                            <Switch
                                checked={formData.two_factor_auth === 1}
                                onChange={setBit('two_factor_auth')}
                                label="Two-factor authentication"
                            />
                        </SettingRow>
                        <div
                            className={twoFactorOn ? '' : 'pointer-events-none opacity-50'}
                            aria-disabled={!twoFactorOn}
                        >
                            <div className="text-sm font-medium text-slate-800">One-time code delivery</div>
                            <p className="mt-0.5 mb-2.5 text-xs leading-relaxed text-slate-500">
                                Email when none selected. SMS and WhatsApp need a phone number on the user.
                            </p>
                            <div className="flex flex-wrap items-center gap-2">
                                {MFA_CHANNEL_OPTIONS.map((opt) => {
                                    const selected = mfaChannels.includes(opt.value);
                                    return (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            aria-pressed={selected}
                                            disabled={!twoFactorOn}
                                            onClick={() => toggleMfaChannel(opt.value)}
                                            className={`rounded-md border px-3 py-1 text-sm transition-colors ${
                                                selected
                                                    ? 'border-primary-600 bg-primary-600 text-white'
                                                    : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'
                                            }`}
                                        >
                                            {opt.label}
                                        </button>
                                    );
                                })}
                            </div>
                            {mfaChannels.length > 1 && (
                                <div className="mt-2.5 flex items-center gap-2">
                                    <span className="text-xs text-slate-500">Default</span>
                                    <select
                                        value={config.mfa_default_channel || mfaChannels[0]}
                                        disabled={!twoFactorOn}
                                        onChange={(e) =>
                                            updateConfig({ ...config, mfa_default_channel: e.target.value })
                                        }
                                        className="rounded-md border border-slate-200 px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                    >
                                        {mfaChannels.map((c) => {
                                            const o = MFA_CHANNEL_OPTIONS.find((x) => x.value === c);
                                            return (
                                                <option key={c} value={c}>
                                                    {o ? o.label : c}
                                                </option>
                                            );
                                        })}
                                    </select>
                                </div>
                            )}
                        </div>
                        {flagRow('totp_mfa_enabled')}
                        {flagRow('sso_enabled')}
                        {flagRow('password_history_count')}
                        {flagRow('enforce_password_expiry')}
                    </SettingsGrid>
                </SettingsSection>

                <SettingsSection
                    id="app-delivery"
                    title="Email & SMS"
                    hint="The Microsoft Graph account that sends this app's emails, and the AWS SNS credentials for SMS one-time codes."
                >
                    <div className="space-y-4">
                        {/* Microsoft Graph credentials for this app's emails (services/mailer.js) */}
                        <EmailProviderSettings provider="graph" appId={appId} />
                        {/* AWS SNS credentials for SMS OTP (services/sms.service.js) */}
                        <EmailProviderSettings
                            provider="sns"
                            appId={appId}
                            title="SMS (AWS SNS)"
                            description="AWS credentials used to text login one-time codes. Without them, SMS codes fall back to email. The IAM user needs sns:Publish."
                        />
                    </div>
                </SettingsSection>

                <SettingsSection id="app-sessions" title="Sessions">
                    <SettingsGrid>
                        <SettingField
                            label={isCreate ? 'Refresh token expiry *' : 'Refresh token expiry'}
                            hint="Duration such as 1d, 12h or 30d."
                        >
                            <input
                                type="text"
                                required={isCreate}
                                value={formData.refresh_token_expiry}
                                onChange={(e) => set('refresh_token_expiry')(e.target.value)}
                                className={`w-32 ${INPUT_CLASS.replace('w-full ', '')}`}
                                placeholder="1d"
                            />
                        </SettingField>
                        {flagRow('session_idle_timeout_minutes')}
                        <SettingRow label="Parallel login" hint="Allow multiple concurrent sessions per user.">
                            <Switch
                                checked={formData.parallel_login === 1}
                                onChange={setBit('parallel_login')}
                                label="Parallel login"
                            />
                        </SettingRow>
                    </SettingsGrid>
                </SettingsSection>

                <SettingsSection id="app-access" title="Access control">
                    <SettingsGrid>
                        {flagRow('scope_rbac_enabled')}
                        {flagRow('org_hierarchy_enabled')}
                        {flagRow('user_groups_enabled')}
                        {flagRow('delegated_access_enabled')}
                        {flagRow('external_users_enabled')}
                        <SettingRow label="Enact user" hint="Allow admins to impersonate users.">
                            <Switch
                                checked={formData.enact_user === 1}
                                onChange={setBit('enact_user')}
                                label="Enact user"
                            />
                        </SettingRow>
                    </SettingsGrid>
                </SettingsSection>

                <SettingsSection id="app-audit" title="Audit & integrations">
                    <SettingsGrid>
                        {flagRow('audit_trail_enabled')}
                        {flagRow('audit_retention_years', { muted: !config.audit_trail_enabled })}
                        {flagRow('webhooks_enabled')}
                        {flagRow('bulk_operations_enabled')}
                    </SettingsGrid>
                    {config.webhooks_enabled && <WebhookEndpointsPanel config={config} updateConfig={updateConfig} />}
                </SettingsSection>

                <SettingsSection
                    id="app-tokens"
                    title="Tokens & claims"
                    hint="How this app's JWTs are signed and what their identity claims are called."
                >
                    <TokenClaimsPanel config={config} updateConfig={updateConfig}>
                        {flagRow('slim_access_token')}
                    </TokenClaimsPanel>
                </SettingsSection>

                <SettingsSection
                    id="app-advanced"
                    title="Advanced"
                    hint="Raw app_config JSON. Everything above writes here; edit directly only for keys without a control."
                >
                    <textarea
                        rows={10}
                        value={formData.app_config}
                        onChange={(e) => set('app_config')(e.target.value)}
                        className={`font-mono text-xs ${INPUT_CLASS}`}
                        placeholder='{"key": "value"}'
                    />
                </SettingsSection>
            </div>
        </div>
    );
};

// tinyint flags arrive as numbers, older rows/clients may send strings.
const isOn = (v) => v === 1 || v === '1' || v === true;

// Enabled app settings + app_config feature flags, as short labels for the table.
const enabledSettings = (app) => {
    const cfg = app.app_config && typeof app.app_config === 'object' ? app.app_config : parseConfig(app.app_config);
    const base = [
        ['Parallel login', app.parallel_login],
        ['Enact user', app.enact_user],
        ['2FA', app.two_factor_auth],
        ['Emails', app.enable_emails],
    ]
        .filter(([, v]) => isOn(v))
        .map(([label]) => label);
    const flags = FEATURE_FLAGS.filter((f) => {
        if (f.type === 'toggle') return cfg[f.key] === true;
        if (f.type === 'number') return cfg[f.key] > 0;
        return false;
    }).map((f) => (f.type === 'number' ? `${f.label}: ${cfg[f.key]}` : f.label));
    return [...base, ...flags];
};

const AppsPage = () => {
    const { refreshApps } = useApps();
    const notice = useNotice();
    const confirm = useConfirm();
    // Global actions (create app, enable/disable/delete any app, central OAuth) are
    // portal-admin only and gated in the UI to match the backend portalAdminRole gate.
    // App admins still edit their own app's config + per-app OAuth (adminRole).
    const isPortalAdmin = getIsPortalAdmin();
    const [localError, setLocalError] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [selectedApp, setSelectedApp] = useState(null);
    const [formData, setFormData] = useState({
        app_name: '',
        app_config: '{}',
        allowed_origins: '',
        refresh_token_expiry: '1d',
        parallel_login: 1,
        enact_user: 0,
        two_factor_auth: 1,
        is_multi_tenant: 0,
        enable_emails: 0,
        tenant_id_prefix: 'INF',
        username: '',
        user_config: null,
    });
    const [userSelectionMode, setUserSelectionMode] = useState('existing');
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [newUserFormData, setNewUserFormData] = useState({
        username: '',
        email_id: '',
        first_name: '',
        last_name: '',
        phone_no: '',
        gender: '',
    });
    // One-time temp-password credential returned when a brand-new admin user is created,
    // surfaced persistently so the portal admin can relay it regardless of email state.
    const [createdCredential, setCreatedCredential] = useState(null);
    const [credCopied, setCredCopied] = useState(false);
    const [createAppStep, setCreateAppStep] = useState(1);
    // Initial admin candidates: any active user, regardless of app association. Loaded when
    // the "existing user" step is shown.
    const usersQuery = useAppUsers(ALL_APPS, {
        enabled: showCreateModal && userSelectionMode === 'existing' && createAppStep === 2,
    });
    const allUsers = usersQuery.items;

    // OAuth / Social login config state
    const [showOAuthModal, setShowOAuthModal] = useState(false);
    const [oauthApp, setOauthApp] = useState(null); // app whose config is being edited
    const [oauthConfigs, setOauthConfigs] = useState([]); // fetched configs from API
    const [oauthLoading, setOauthLoading] = useState(false);
    const [oauthSaving, setOauthSaving] = useState(''); // provider name while saving
    const [oauthError, setOauthError] = useState('');
    const [oauthSuccess, setOauthSuccess] = useState('');
    const [showPrivateKey, setShowPrivateKey] = useState({}); // { google: false, apple: false }
    // Local editable form state per provider
    const [oauthForms, setOauthForms] = useState({
        google: { is_enabled: 0, client_id: '', changed: false },
        apple: { is_enabled: 0, client_id: '', team_id: '', key_id: '', private_key: '', changed: false },
    });
    // Central (fallback) configs returned alongside app configs
    const [oauthCentralConfigs, setOauthCentralConfigs] = useState([]);

    // Central OAuth config modal state (portal-admin only, platform-wide defaults)
    const [showCentralOAuthModal, setShowCentralOAuthModal] = useState(false);
    const [centralOauthConfigs, setCentralOauthConfigs] = useState([]);
    const [centralOauthLoading, setCentralOauthLoading] = useState(false);
    const [centralOauthSaving, setCentralOauthSaving] = useState('');
    const [centralOauthError, setCentralOauthError] = useState('');
    const [centralOauthSuccess, setCentralOauthSuccess] = useState('');
    const [centralShowPrivateKey, setCentralShowPrivateKey] = useState({});
    const [centralOauthForms, setCentralOauthForms] = useState({
        google: { is_enabled: 0, client_id: '', changed: false },
        apple: { is_enabled: 0, client_id: '', team_id: '', key_id: '', private_key: '', changed: false },
    });

    // Signing Keys panel state (portal-admin only)
    const [showSigningKeysModal, setShowSigningKeysModal] = useState(false);
    const [signingKeysApp, setSigningKeysApp] = useState(null); // app whose keys are being viewed
    const [signingKeys, setSigningKeys] = useState({}); // { [app_id]: { AUTHORISE_JWT_KEY: bool, ... } }
    const [signingKeysLoading, setSigningKeysLoading] = useState(false);
    const [signingKeysActionType, setSigningKeysActionType] = useState(''); // secret_type currently in-flight

    // URL keys = lib/paginate filter keys: status, multi_tenant, two_factor, emails, created_*.
    const columns = [
        {
            key: 'app_name',
            header: 'Application',
            sortKey: 'name',
            locked: true,
            render: (app) => (
                <div className="min-w-0">
                    <div className="font-medium text-slate-900">{app.app_name}</div>
                    <CopyableId label="Copy app ID" value={app.app_id} />
                </div>
            ),
        },
        {
            key: 'status',
            header: 'Status',
            sortKey: 'status',
            render: (app) => <StatusBadge active={Boolean(app.is_active)} />,
            filter: { type: 'multiselect', options: STATUS_OPTIONS, defaultExpanded: true },
        },
        {
            key: 'multi_tenant',
            header: 'Multi-tenant',
            sortKey: 'multi_tenant',
            render: (app) => (isOn(app.is_multi_tenant) ? 'Yes' : 'No'),
            filter: {
                type: 'boolean',
                trueLabel: 'Multi-tenant',
                falseLabel: 'Single-tenant',
                defaultExpanded: true,
            },
        },
        {
            key: 'two_factor',
            header: 'Two-factor sign-in',
            defaultVisible: false,
            render: (app) => (isOn(app.two_factor_auth) ? 'On' : 'Off'),
            filter: { type: 'boolean', trueLabel: 'On', falseLabel: 'Off' },
        },
        {
            key: 'emails',
            header: 'Emails',
            defaultVisible: false,
            render: (app) => (isOn(app.enable_emails) ? 'On' : 'Off'),
            filter: { type: 'boolean', trueLabel: 'On', falseLabel: 'Off' },
        },
        {
            key: 'created',
            header: 'Created',
            sortKey: 'created',
            defaultVisible: false,
            className: 'whitespace-nowrap text-slate-500',
            render: (app) => (app.created_on ? formatDate(app.created_on) : '—'),
            filter: { type: 'date' },
        },
        {
            key: 'refresh',
            header: 'Refresh token',
            sortKey: 'refresh',
            render: (app) => app.refresh_token_expiry || '—',
        },
        {
            key: 'settings',
            header: 'Enabled settings',
            render: (app) => {
                const list = enabledSettings(app);
                if (!list.length) return <span className="text-slate-400">None</span>;
                return (
                    <div className="flex flex-wrap gap-1 max-w-sm" title={list.join(', ')}>
                        {list.map((label) => (
                            <span
                                key={label}
                                className="text-xs px-1.5 py-0.5 rounded bg-slate-100 text-slate-700 whitespace-nowrap"
                            >
                                {label}
                            </span>
                        ))}
                    </div>
                );
            },
        },
        {
            key: 'links',
            header: 'Open',
            label: 'Quick links',
            render: (app) => (
                <div className="flex items-center gap-0.5">
                    <RowLink to={appLink('users', app)} icon={ENTITY_ICONS.users} label="Users" />
                    {isOn(app.is_multi_tenant) && (
                        <RowLink to={appLink('tenants', app)} icon={ENTITY_ICONS.tenants} label="Tenants" />
                    )}
                    <RowLink to={appLink('roles', app)} icon={ENTITY_ICONS.roles} label="Roles" />
                    <RowLink to={appLink('components', app)} icon={ENTITY_ICONS.components} label="Components" />
                    <RowLink to={appLink('invitations', app)} icon={ENTITY_ICONS.invitations} label="Invitations" />
                    <RowLink to={appLink('api-keys', app)} icon={ENTITY_ICONS.apiKeys} label="API Keys" />
                </div>
            ),
        },
        {
            key: 'actions',
            header: <span className="sr-only">Actions</span>,
            label: 'Row actions',
            locked: true,
            align: 'right',
            render: (app) => (
                <RowActions>
                    <RowAction icon={Edit2} label="Edit app" onClick={() => openUpdateModal(app)} />
                    <RowAction icon={LogIn} label="Social login" tone="info" onClick={() => openOAuthModal(app)} />
                    {isPortalAdmin && (
                        <>
                            <RowAction
                                icon={KeyRound}
                                label="Signing keys"
                                tone="info"
                                onClick={() => openSigningKeysModal(app)}
                            />
                            <RowAction
                                icon={Power}
                                label={app.is_active ? 'Deactivate app' : 'Activate app'}
                                tone={app.is_active ? 'warning' : 'success'}
                                onClick={() => handleToggleStatus(app, app.is_active)}
                            />
                            <RowAction
                                icon={Trash2}
                                label="Delete app"
                                tone="danger"
                                onClick={() => handleDeleteApp(app)}
                            />
                        </>
                    )}
                </RowActions>
            ),
        },
    ];

    // App-scoped admins page /get-my-apps (/get-all-apps is portal-admin only).
    const table = useServerTable((query) => appsService.getPage(query, { mine: !isPortalAdmin }), {
        defaultSort: { key: 'name', dir: 'asc' },
        columns,
    });

    // The table and the app selectors on every other page (AppsContext) both list apps.
    const refreshList = () => {
        table.reload();
        refreshApps();
    };

    const flash = (message) => notice.success(message);

    // Tenant ID prefix inline validation (multi-tenant apps only)
    const tenantIdPrefixValid = TENANT_ID_PREFIX_PATTERN.test(formData.tenant_id_prefix || '');

    const filteredUsers = useMemo(() => {
        if (!userSearchTerm) return allUsers;
        const search = userSearchTerm.toLowerCase();
        return allUsers.filter(
            (user) =>
                user.username?.toLowerCase().includes(search) ||
                user.email_id?.toLowerCase().includes(search) ||
                `${user.first_name || ''} ${user.last_name || ''}`.toLowerCase().includes(search)
        );
    }, [allUsers, userSearchTerm]);

    const userOptions = useMemo(() => {
        return filteredUsers.map((user) => ({
            value: user.username,
            label: user.username,
            subtitle: `${user.first_name || ''} ${user.last_name || ''} ${
                user.email_id ? `(${user.email_id})` : ''
            }`.trim(),
        }));
    }, [filteredUsers]);

    const handleCreateApp = async (e) => {
        e.preventDefault();
        setLocalError('');

        if (userSelectionMode === 'existing' && !formData.username) {
            setLocalError('Please select an existing user');
            return;
        }

        if (userSelectionMode === 'new') {
            if (
                !newUserFormData.username ||
                !newUserFormData.email_id ||
                !newUserFormData.first_name ||
                !newUserFormData.last_name
            ) {
                setLocalError('Please fill in all required fields for the new user');
                return;
            }
        }

        try {
            const parsedConfig = JSON.parse(formData.app_config || '{}');
            const origins = formData.allowed_origins
                .split(',')
                .map((o) => o.trim())
                .filter(Boolean);
            if (origins.length > 0) {
                parsedConfig.allowed_origins = origins;
            } else {
                delete parsedConfig.allowed_origins;
            }
            if (formData.tenant_id_prefix && formData.tenant_id_prefix.trim()) {
                parsedConfig.tenant_id_prefix = formData.tenant_id_prefix.trim();
            } else {
                delete parsedConfig.tenant_id_prefix;
            }
            const payload = {
                app_name: formData.app_name,
                app_config: parsedConfig,
                refresh_token_expiry: formData.refresh_token_expiry,
                parallel_login: Number(formData.parallel_login),
                enact_user: Number(formData.enact_user),
                two_factor_auth: Number(formData.two_factor_auth),
                is_multi_tenant: Number(formData.is_multi_tenant),
                enable_emails: Number(formData.enable_emails),
            };
            if (userSelectionMode === 'existing' && formData.username) {
                payload.username = formData.username;
            } else if (userSelectionMode === 'new') {
                payload.user_config = {
                    username: newUserFormData.username,
                    email_id: newUserFormData.email_id,
                    first_name: newUserFormData.first_name,
                    last_name: newUserFormData.last_name,
                    phone_no: newUserFormData.phone_no || undefined,
                    gender: newUserFormData.gender || undefined,
                };
            }
            const result = await appsService.createApp(payload);
            const created = result?.data || {};
            const newAdminUsername = userSelectionMode === 'new' ? newUserFormData.username : null;
            setShowCreateModal(false);
            resetForm();
            refreshList();
            if (created.temp_password) {
                // Show credentials persistently (until dismissed) — do NOT auto-clear.
                setCreatedCredential({
                    username: newAdminUsername,
                    tempPassword: created.temp_password,
                    emailSent: created.email_sent,
                });
            } else {
                flash('App created.');
            }
        } catch (err) {
            setLocalError(err?.response?.data?.message || err?.message || 'Failed to create app');
        }
    };

    const handleCopyCredential = () => {
        if (!createdCredential?.tempPassword || !navigator.clipboard) return;
        navigator.clipboard.writeText(createdCredential.tempPassword).then(() => {
            setCredCopied(true);
            setTimeout(() => setCredCopied(false), 2000);
        });
    };

    const handleUpdateApp = async (e) => {
        e.preventDefault();
        if (!selectedApp) return;
        setLocalError('');
        try {
            const payload = {};
            if (formData.app_name && formData.app_name.trim()) payload.app_name = formData.app_name.trim();
            if (formData.app_config) {
                const parsedConfig = JSON.parse(formData.app_config);
                const origins = formData.allowed_origins
                    .split(',')
                    .map((o) => o.trim())
                    .filter(Boolean);
                if (origins.length > 0) {
                    parsedConfig.allowed_origins = origins;
                } else {
                    delete parsedConfig.allowed_origins;
                }
                if (formData.tenant_id_prefix && formData.tenant_id_prefix.trim()) {
                    parsedConfig.tenant_id_prefix = formData.tenant_id_prefix.trim();
                } else {
                    delete parsedConfig.tenant_id_prefix;
                }
                payload.app_config = parsedConfig;
            }
            if (formData.refresh_token_expiry) payload.refresh_token_expiry = formData.refresh_token_expiry;
            if (formData.parallel_login !== '') payload.parallel_login = Number(formData.parallel_login);
            if (formData.enact_user !== '') payload.enact_user = Number(formData.enact_user);
            if (formData.two_factor_auth !== '') payload.two_factor_auth = Number(formData.two_factor_auth);
            if (formData.is_multi_tenant !== '') payload.is_multi_tenant = Number(formData.is_multi_tenant);
            if (formData.enable_emails !== '') payload.enable_emails = Number(formData.enable_emails);
            await appsService.updateAppDetails(selectedApp.app_id, payload);
            setShowUpdateModal(false);
            setSelectedApp(null);
            resetForm();
            refreshList();
            flash(`${selectedApp.app_name} updated.`);
        } catch (err) {
            setLocalError(err?.response?.data?.message || err?.message || 'Failed to update app');
        }
    };

    const resetForm = () => {
        setFormData({
            app_name: '',
            app_config: '{}',
            allowed_origins: '',
            refresh_token_expiry: '1d',
            parallel_login: 1,
            enact_user: 0,
            two_factor_auth: 1,
            is_multi_tenant: 0,
            enable_emails: 0,
            tenant_id_prefix: 'INF',
            username: '',
            user_config: null,
        });
        setUserSelectionMode('existing');
        setNewUserFormData({
            username: '',
            email_id: '',
            first_name: '',
            last_name: '',
            phone_no: '',
            gender: '',
        });
        setUserSearchTerm('');
        setCreateAppStep(1);
        setLocalError('');
    };

    const validateStep1 = () => {
        if (!formData.app_name.trim()) {
            setLocalError('App name is required');
            return false;
        }
        if (!formData.app_config.trim()) {
            setLocalError('App config is required');
            return false;
        }
        let parsedConfig;
        try {
            parsedConfig = JSON.parse(formData.app_config);
        } catch (e) {
            setLocalError('App config must be valid JSON');
            return false;
        }
        // Same rules the API enforces (see services/common.utils.js:validateClaimProfile).
        // Caught here too so a bad profile can't be authored via the raw JSON box either.
        const claimProfileErrors = validateClaimProfile(parsedConfig && parsedConfig.claim_profile);
        if (claimProfileErrors.length) {
            setLocalError(`Token & Claims: ${claimProfileErrors[0]}`);
            return false;
        }
        setLocalError('');
        return true;
    };

    const handleNextStep = () => {
        if (validateStep1()) {
            setCreateAppStep(2);
        }
    };

    const handleBackStep = () => {
        setCreateAppStep(1);
        setLocalError('');
    };

    const openUpdateModal = (app) => {
        setSelectedApp(app);
        const existingConfig = app.app_config || {};
        const originsArray = existingConfig.allowed_origins || [];
        setFormData({
            app_name: app.app_name || '',
            app_config: JSON.stringify(existingConfig, null, 2),
            allowed_origins: Array.isArray(originsArray) ? originsArray.join(', ') : '',
            refresh_token_expiry: app.refresh_token_expiry || '1d',
            parallel_login: app.parallel_login ?? 1,
            enact_user: app.enact_user ?? 0,
            two_factor_auth: app.two_factor_auth ?? 1,
            is_multi_tenant: app.is_multi_tenant ?? 0,
            enable_emails: app.enable_emails ?? 0,
            tenant_id_prefix: existingConfig?.tenant_id_prefix || 'INF',
            username: '',
            user_config: null,
        });
        setShowUpdateModal(true);
    };

    const handleDeleteApp = async (app) => {
        const ok = await confirm({
            title: `Delete app ${app.app_name}?`,
            body: 'The app and all of its data are permanently deleted. This can’t be undone.',
            impact: [
                'All tenants (for multi-tenant apps)',
                'All user mappings',
                'All roles and role mappings',
                'All components',
                'All authentication sessions',
            ],
            confirmLabel: 'Delete',
            tone: 'danger',
            typeToConfirm: app.app_name,
        });
        if (!ok) return;
        try {
            await appsService.deleteApp(app.app_id);
            refreshList();
            flash(`App ${app.app_name} deleted.`);
        } catch (err) {
            notice.error(err, `Couldn’t delete app ${app.app_name}. Try again.`);
        }
    };

    // ── OAuth config handlers ──────────────────────────────────────────────────

    const openOAuthModal = useCallback(async (app) => {
        setOauthApp(app);
        setOauthError('');
        setOauthSuccess('');
        setShowOAuthModal(true);
        setOauthLoading(true);
        try {
            const { data: configs, centralConfigs } = await oauthConfigService.getOAuthConfigs(app.app_id);
            setOauthConfigs(configs);
            setOauthCentralConfigs(centralConfigs);
            // Populate local form state from fetched configs
            const forms = {
                google: { is_enabled: 0, client_id: '', changed: false },
                apple: { is_enabled: 0, client_id: '', team_id: '', key_id: '', private_key: '', changed: false },
            };
            configs.forEach((cfg) => {
                if (cfg.provider === 'google') {
                    forms.google = { is_enabled: cfg.is_enabled, client_id: cfg.client_id || '', changed: false };
                }
                if (cfg.provider === 'apple') {
                    forms.apple = {
                        is_enabled: cfg.is_enabled,
                        client_id: cfg.client_id || '',
                        team_id: cfg.team_id || '',
                        key_id: cfg.key_id || '',
                        private_key: '', // never pre-fill the private key
                        changed: false,
                    };
                }
            });
            setOauthForms(forms);
        } catch (err) {
            setOauthError(err?.response?.data?.message || 'Failed to load OAuth configuration');
        } finally {
            setOauthLoading(false);
        }
    }, []);

    const handleOAuthFormChange = (provider, field, value) => {
        setOauthForms((prev) => ({
            ...prev,
            [provider]: { ...prev[provider], [field]: value, changed: true },
        }));
    };

    const refreshAppOAuthConfigs = useCallback(async (appId) => {
        const { data: configs, centralConfigs } = await oauthConfigService.getOAuthConfigs(appId);
        setOauthConfigs(configs);
        setOauthCentralConfigs(centralConfigs);
        return configs;
    }, []);

    const handleSaveOAuthProvider = async (provider) => {
        setOauthSaving(provider);
        setOauthError('');
        setOauthSuccess('');
        try {
            const form = oauthForms[provider];
            const payload = { provider, is_enabled: form.is_enabled };
            if (form.client_id) payload.client_id = form.client_id;
            if (provider === 'apple') {
                if (form.team_id) payload.team_id = form.team_id;
                if (form.key_id) payload.key_id = form.key_id;
                if (form.private_key) payload.private_key = form.private_key;
            }
            await oauthConfigService.upsertOAuthConfig(oauthApp.app_id, payload);
            setOauthSuccess(`${provider.charAt(0).toUpperCase() + provider.slice(1)} configuration saved.`);
            setOauthForms((prev) => ({ ...prev, [provider]: { ...prev[provider], changed: false } }));
            await refreshAppOAuthConfigs(oauthApp.app_id);
            setTimeout(() => setOauthSuccess(''), 3000);
        } catch (err) {
            setOauthError(err?.response?.data?.message || `Failed to save ${provider} configuration`);
        } finally {
            setOauthSaving('');
        }
    };

    const handleDeleteOAuthProvider = async (provider) => {
        const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);
        const ok = await confirm({
            title: `Delete the ${providerName} sign-in configuration for ${oauthApp?.app_name}?`,
            body: `${providerName} sign-in for this app falls back to the platform default configuration, if one is set. Otherwise the ${providerName} button no longer appears.`,
            confirmLabel: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setOauthSaving(provider);
        setOauthError('');
        try {
            await oauthConfigService.deleteOAuthConfig(oauthApp.app_id, provider);
            setOauthSuccess(`${provider.charAt(0).toUpperCase() + provider.slice(1)} configuration removed.`);
            setOauthForms((prev) => ({
                ...prev,
                [provider]:
                    provider === 'google'
                        ? { is_enabled: 0, client_id: '', changed: false }
                        : { is_enabled: 0, client_id: '', team_id: '', key_id: '', private_key: '', changed: false },
            }));
            await refreshAppOAuthConfigs(oauthApp.app_id);
            setTimeout(() => setOauthSuccess(''), 3000);
        } catch (err) {
            setOauthError(err?.response?.data?.message || `Failed to remove ${provider} configuration`);
        } finally {
            setOauthSaving('');
        }
    };

    // ── Central OAuth config handlers ─────────────────────────────────────────

    const openCentralOAuthModal = useCallback(async () => {
        setCentralOauthError('');
        setCentralOauthSuccess('');
        setShowCentralOAuthModal(true);
        setCentralOauthLoading(true);
        try {
            const configs = await oauthConfigService.getCentralOAuthConfigs();
            setCentralOauthConfigs(configs);
            const forms = {
                google: { is_enabled: 0, client_id: '', changed: false },
                apple: { is_enabled: 0, client_id: '', team_id: '', key_id: '', private_key: '', changed: false },
            };
            configs.forEach((cfg) => {
                if (cfg.provider === 'google') {
                    forms.google = { is_enabled: cfg.is_enabled, client_id: cfg.client_id || '', changed: false };
                }
                if (cfg.provider === 'apple') {
                    forms.apple = {
                        is_enabled: cfg.is_enabled,
                        client_id: cfg.client_id || '',
                        team_id: cfg.team_id || '',
                        key_id: cfg.key_id || '',
                        private_key: '',
                        changed: false,
                    };
                }
            });
            setCentralOauthForms(forms);
        } catch (err) {
            setCentralOauthError(err?.response?.data?.message || 'Failed to load central OAuth configuration');
        } finally {
            setCentralOauthLoading(false);
        }
    }, []);

    const handleCentralOAuthFormChange = (provider, field, value) => {
        setCentralOauthForms((prev) => ({
            ...prev,
            [provider]: { ...prev[provider], [field]: value, changed: true },
        }));
    };

    const handleSaveCentralOAuthProvider = async (provider) => {
        setCentralOauthSaving(provider);
        setCentralOauthError('');
        setCentralOauthSuccess('');
        try {
            const form = centralOauthForms[provider];
            const payload = { provider, is_enabled: form.is_enabled };
            if (form.client_id) payload.client_id = form.client_id;
            if (provider === 'apple') {
                if (form.team_id) payload.team_id = form.team_id;
                if (form.key_id) payload.key_id = form.key_id;
                if (form.private_key) payload.private_key = form.private_key;
            }
            await oauthConfigService.upsertCentralOAuthConfig(payload);
            setCentralOauthSuccess(
                `${provider.charAt(0).toUpperCase() + provider.slice(1)} default configuration saved.`
            );
            setCentralOauthForms((prev) => ({ ...prev, [provider]: { ...prev[provider], changed: false } }));
            const configs = await oauthConfigService.getCentralOAuthConfigs();
            setCentralOauthConfigs(configs);
            setTimeout(() => setCentralOauthSuccess(''), 3000);
        } catch (err) {
            setCentralOauthError(err?.response?.data?.message || `Failed to save ${provider} default configuration`);
        } finally {
            setCentralOauthSaving('');
        }
    };

    const handleDeleteCentralOAuthProvider = async (provider) => {
        const providerName = provider.charAt(0).toUpperCase() + provider.slice(1);
        const ok = await confirm({
            title: `Delete the default ${providerName} sign-in configuration?`,
            body: `Apps without their own ${providerName} configuration use this one, so they lose ${providerName} sign-in.`,
            confirmLabel: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setCentralOauthSaving(provider);
        setCentralOauthError('');
        try {
            await oauthConfigService.deleteCentralOAuthConfig(provider);
            setCentralOauthSuccess(
                `${provider.charAt(0).toUpperCase() + provider.slice(1)} default configuration removed.`
            );
            setCentralOauthForms((prev) => ({
                ...prev,
                [provider]:
                    provider === 'google'
                        ? { is_enabled: 0, client_id: '', changed: false }
                        : { is_enabled: 0, client_id: '', team_id: '', key_id: '', private_key: '', changed: false },
            }));
            const configs = await oauthConfigService.getCentralOAuthConfigs();
            setCentralOauthConfigs(configs);
            setTimeout(() => setCentralOauthSuccess(''), 3000);
        } catch (err) {
            setCentralOauthError(err?.response?.data?.message || `Failed to remove ${provider} default configuration`);
        } finally {
            setCentralOauthSaving('');
        }
    };

    const handleToggleStatus = async (app, currentStatus) => {
        const ok = await confirm({
            title: `${currentStatus ? 'Deactivate' : 'Activate'} app ${app.app_name}?`,
            body: currentStatus
                ? 'The app is unavailable until it is activated again. Its users, roles and data are kept.'
                : 'The app becomes available again with its existing users, roles and data.',
            confirmLabel: currentStatus ? 'Deactivate' : 'Activate',
            tone: currentStatus ? 'danger' : 'default',
        });
        if (!ok) return;
        setLocalError('');
        try {
            await appsService.updateAppStatus(app.app_id, !currentStatus);
            refreshList();
            flash(`App ${app.app_name} ${currentStatus ? 'deactivated' : 'activated'}.`);
        } catch (err) {
            notice.error(err, `Couldn’t ${currentStatus ? 'deactivate' : 'activate'} ${app.app_name}. Try again.`);
        }
    };

    // ── Signing Keys handlers (portal-admin only) ─────────────────────────────

    const fetchSigningKeys = useCallback(async (appId) => {
        setSigningKeysLoading(true);
        setLocalError('');
        try {
            const data = await appsService.getSigningKeys(appId);
            setSigningKeys((prev) => ({ ...prev, [appId]: data || {} }));
        } catch (err) {
            setLocalError(err?.response?.data?.message || err?.message || 'Failed to load signing keys');
        } finally {
            setSigningKeysLoading(false);
        }
    }, []);

    const openSigningKeysModal = useCallback(
        (app) => {
            setSigningKeysApp(app);
            setLocalError('');
            setShowSigningKeysModal(true);
            fetchSigningKeys(app.app_id);
        },
        [fetchSigningKeys]
    );

    const handleGenerateSigningKey = async (secretType) => {
        if (!signingKeysApp) return;
        setSigningKeysActionType(secretType);
        setLocalError('');
        try {
            await appsService.generateSigningKey(signingKeysApp.app_id, secretType);
            await fetchSigningKeys(signingKeysApp.app_id);
        } catch (err) {
            setLocalError(err?.response?.data?.message || err?.message || 'Failed to generate signing key');
        } finally {
            setSigningKeysActionType('');
        }
    };

    const handleRotateSigningKey = async (secretType) => {
        if (!signingKeysApp) return;
        const ok = await confirm({
            title: `Rotate the ${(
                SIGNING_KEY_TYPES.find((k) => k.type === secretType)?.label || 'signing'
            ).toLowerCase()} key for ${signingKeysApp.app_name}?`,
            body: 'Tokens signed with the current key stop verifying, so active sessions for this app are signed out.',
            confirmLabel: 'Rotate',
            tone: 'danger',
        });
        if (!ok) return;
        setSigningKeysActionType(secretType);
        setLocalError('');
        try {
            await appsService.rotateSigningKey(signingKeysApp.app_id, secretType);
            await fetchSigningKeys(signingKeysApp.app_id);
        } catch (err) {
            setLocalError(err?.response?.data?.message || err?.message || 'Failed to rotate signing key');
        } finally {
            setSigningKeysActionType('');
        }
    };

    const handleRemoveSigningKey = async (secretType) => {
        if (!signingKeysApp) return;
        const ok = await confirm({
            title: `Delete the ${(
                SIGNING_KEY_TYPES.find((k) => k.type === secretType)?.label || 'signing'
            ).toLowerCase()} key for ${signingKeysApp.app_name}?`,
            body: 'The app falls back to the platform core key. Tokens signed with the current key stop verifying, so active sessions for this app are signed out.',
            confirmLabel: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setSigningKeysActionType(secretType);
        setLocalError('');
        try {
            await appsService.removeSigningKey(signingKeysApp.app_id, secretType);
            await fetchSigningKeys(signingKeysApp.app_id);
        } catch (err) {
            setLocalError(err?.response?.data?.message || err?.message || 'Failed to remove signing key');
        } finally {
            setSigningKeysActionType('');
        }
    };

    const appLink = (path, app) => `/${path}?app_id=${encodeURIComponent(app.app_id)}`;


    return (
        <div className="space-y-4">
            <PageHeader
                title="Applications"
                subtitle="Manage registered applications"
                actions={
                    isPortalAdmin ? (
                        <div className="flex items-center gap-2">
                            <button
                                onClick={openCentralOAuthModal}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-sm font-medium hover:bg-indigo-100"
                            >
                                <Globe className="h-4 w-4" /> Default Social Login
                            </button>
                            <button
                                onClick={() => {
                                    resetForm();
                                    setCreateAppStep(1);
                                    setShowCreateModal(true);
                                }}
                                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700"
                            >
                                <Plus className="h-4 w-4" /> Add App
                            </button>
                        </div>
                    ) : null
                }
            />

            {createdCredential && (
                <div className="rounded-lg bg-amber-50 border border-amber-300 text-amber-900 px-4 py-3 text-sm space-y-2">
                    <div className="flex items-center gap-2 font-semibold">
                        <KeyRound className="h-4 w-4" /> App created — temporary admin credentials
                    </div>
                    <p className="text-amber-800">
                        {createdCredential.emailSent
                            ? 'An onboarding email was sent. Keep these as a backup in case it is not received — they are shown only once:'
                            : 'Email was not sent. Relay these credentials to the new admin — they are shown only once:'}
                    </p>
                    <div className="flex flex-wrap items-center gap-3">
                        {createdCredential.username && (
                            <span className="font-mono">
                                User: <strong>{createdCredential.username}</strong>
                            </span>
                        )}
                        <span className="font-mono">
                            Temp password: <strong>{createdCredential.tempPassword}</strong>
                        </span>
                        <button
                            type="button"
                            onClick={handleCopyCredential}
                            className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-200 hover:bg-amber-300 text-amber-900 text-xs font-semibold"
                        >
                            {credCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                            {credCopied ? 'Copied' : 'Copy password'}
                        </button>
                        <button
                            type="button"
                            onClick={() => setCreatedCredential(null)}
                            className="text-xs font-semibold text-amber-700 hover:text-amber-900 underline"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            <DataTable
                {...table.tableProps}
                columns={columns}
                tableKey="apps"
                rowKey="app_id"
                searchPlaceholder="Search app name or ID…"
                emptyMessage="No applications found."
            />

            {/* Create App Modal */}
            <Modal
                isOpen={showCreateModal}
                onClose={() => {
                    resetForm();
                    setShowCreateModal(false);
                }}
                title="Create New Application"
                size="full"
                footer={
                    <div className="flex items-center gap-3">
                        <ol className="flex items-center gap-2 text-sm">
                            {['App settings', 'Admin user'].map((label, i) => (
                                <li key={label} className="flex items-center gap-2">
                                    {i > 0 && <span className="h-px w-6 bg-slate-200" />}
                                    <span
                                        className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-medium ${
                                            createAppStep >= i + 1
                                                ? 'bg-primary-600 text-white'
                                                : 'bg-slate-100 text-slate-500'
                                        }`}
                                    >
                                        {i + 1}
                                    </span>
                                    <span className={createAppStep === i + 1 ? 'text-slate-900' : 'text-slate-500'}>
                                        {label}
                                    </span>
                                </li>
                            ))}
                        </ol>
                        {localError && <p className="ml-4 flex-1 truncate text-sm text-red-600">{localError}</p>}
                        <div className="ml-auto flex gap-2">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    resetForm();
                                }}
                                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
                            >
                                Cancel
                            </button>
                            {createAppStep === 1 ? (
                                <button
                                    type="button"
                                    onClick={handleNextStep}
                                    disabled={formData.is_multi_tenant === 1 && !tenantIdPrefixValid}
                                    className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                                >
                                    Next: Admin user
                                </button>
                            ) : (
                                <>
                                    <button
                                        type="button"
                                        onClick={handleBackStep}
                                        className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
                                    >
                                        Back
                                    </button>
                                    <button
                                        type="submit"
                                        form="create-app-form"
                                        className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 active:scale-[0.98]"
                                    >
                                        Create app
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                }
            >
                <form id="create-app-form" onSubmit={handleCreateApp}>
                    {createAppStep === 1 ? (
                        <AppSettingsFields
                            formData={formData}
                            setFormData={setFormData}
                            tenantIdPrefixValid={tenantIdPrefixValid}
                            isCreate
                        />
                    ) : (
                        <div className="mx-auto max-w-2xl">
                            <h3 className="mb-4 text-base font-semibold tracking-tight text-slate-900">Admin user</h3>
                            <div>
                                <p className="text-sm font-medium text-slate-700 mb-3">Admin User</p>

                                <div className="flex gap-4 mb-4">
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="userMode"
                                            value="existing"
                                            checked={userSelectionMode === 'existing'}
                                            onChange={(e) => {
                                                setUserSelectionMode(e.target.value);
                                                setFormData({ ...formData, username: '', user_config: null });
                                                setNewUserFormData({
                                                    username: '',
                                                    email_id: '',
                                                    first_name: '',
                                                    last_name: '',
                                                    phone_no: '',
                                                    gender: '',
                                                });
                                            }}
                                            className="text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm font-medium text-slate-700">Existing User</span>
                                    </label>
                                    <label className="flex items-center gap-2 cursor-pointer">
                                        <input
                                            type="radio"
                                            name="userMode"
                                            value="new"
                                            checked={userSelectionMode === 'new'}
                                            onChange={(e) => {
                                                setUserSelectionMode(e.target.value);
                                                setFormData({ ...formData, username: '', user_config: null });
                                            }}
                                            className="text-primary-600 focus:ring-primary-500"
                                        />
                                        <span className="text-sm font-medium text-slate-700">New User</span>
                                    </label>
                                </div>

                                {userSelectionMode === 'existing' ? (
                                    <div>
                                        <label className="block text-sm font-medium text-slate-700 mb-1.5">
                                            Select Existing User <span className="text-red-500">*</span>
                                        </label>
                                        <Typeahead
                                            options={userOptions}
                                            value={formData.username}
                                            onChange={(value) =>
                                                setFormData({
                                                    ...formData,
                                                    username: value || '',
                                                    user_config: null,
                                                })
                                            }
                                            onSearch={(term) => setUserSearchTerm(term)}
                                            placeholder="Search by username, email, or name..."
                                            displayKey="label"
                                            valueKey="value"
                                            loading={usersQuery.loading}
                                        />
                                        {usersQuery.error && (
                                            <p className="text-xs text-red-600 mt-1.5">{usersQuery.error}</p>
                                        )}
                                        {formData.username && (
                                            <p className="text-xs text-slate-500 mt-1.5">
                                                Selected: {formData.username}
                                            </p>
                                        )}
                                    </div>
                                ) : (
                                    <NewUserForm formData={newUserFormData} setFormData={setNewUserFormData} />
                                )}
                            </div>
                        </div>
                    )}
                </form>
            </Modal>

            {/* Update App Modal */}
            <Modal
                isOpen={showUpdateModal}
                onClose={() => setShowUpdateModal(false)}
                title={`Update ${selectedApp?.app_name || 'App'}`}
                size="full"
                footer={
                    <div className="flex items-center gap-3">
                        {localError && <p className="flex-1 truncate text-sm text-red-600">{localError}</p>}
                        <div className="ml-auto flex gap-2">
                            <button
                                type="button"
                                onClick={() => setShowUpdateModal(false)}
                                className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                form="update-app-form"
                                disabled={formData.is_multi_tenant === 1 && !tenantIdPrefixValid}
                                className="rounded-lg bg-primary-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                            >
                                Save changes
                            </button>
                        </div>
                    </div>
                }
            >
                <form id="update-app-form" onSubmit={handleUpdateApp}>
                    <AppSettingsFields
                        formData={formData}
                        setFormData={setFormData}
                        tenantIdPrefixValid={tenantIdPrefixValid}
                        appId={selectedApp?.app_id}
                    />
                </form>
            </Modal>

            {/* OAuth / Social Login Config Modal */}
            <Modal
                isOpen={showOAuthModal}
                onClose={() => {
                    setShowOAuthModal(false);
                    setOauthApp(null);
                    setOauthError('');
                    setOauthSuccess('');
                }}
                title={`Social Login — ${oauthApp?.app_name || ''}`}
                size="lg"
            >
                {oauthLoading ? (
                    <div className="py-12 text-center text-sm text-slate-500">Loading configuration...</div>
                ) : (
                    <div className="space-y-6">
                        <p className="text-sm text-slate-500">
                            Configure which social providers are enabled for this application. Credentials are stored
                            securely and never exposed to end users.
                        </p>

                        {oauthError && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                {oauthError}
                            </div>
                        )}
                        {oauthSuccess && (
                            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                                {oauthSuccess}
                            </div>
                        )}

                        {/* ── Google ─────────────────────────────────────── */}
                        <OAuthProviderSection
                            provider="google"
                            label="Google"
                            form={oauthForms.google}
                            existingConfig={oauthConfigs.find((c) => c.provider === 'google')}
                            centralConfig={oauthCentralConfigs.find((c) => c.provider === 'google')}
                            isSaving={oauthSaving === 'google'}
                            showPrivateKey={showPrivateKey}
                            onTogglePrivateKey={(p) => setShowPrivateKey((prev) => ({ ...prev, [p]: !prev[p] }))}
                            onChange={(field, value) => handleOAuthFormChange('google', field, value)}
                            onSave={() => handleSaveOAuthProvider('google')}
                            onDelete={() => handleDeleteOAuthProvider('google')}
                        />

                        <div className="border-t border-slate-200" />

                        {/* ── Apple ──────────────────────────────────────── */}
                        <OAuthProviderSection
                            provider="apple"
                            label="Apple"
                            form={oauthForms.apple}
                            existingConfig={oauthConfigs.find((c) => c.provider === 'apple')}
                            centralConfig={oauthCentralConfigs.find((c) => c.provider === 'apple')}
                            isSaving={oauthSaving === 'apple'}
                            showPrivateKey={showPrivateKey}
                            onTogglePrivateKey={(p) => setShowPrivateKey((prev) => ({ ...prev, [p]: !prev[p] }))}
                            onChange={(field, value) => handleOAuthFormChange('apple', field, value)}
                            onSave={() => handleSaveOAuthProvider('apple')}
                            onDelete={() => handleDeleteOAuthProvider('apple')}
                        />
                    </div>
                )}
            </Modal>

            {/* Central / Default OAuth Config Modal */}
            <Modal
                isOpen={showCentralOAuthModal}
                onClose={() => {
                    setShowCentralOAuthModal(false);
                    setCentralOauthError('');
                    setCentralOauthSuccess('');
                }}
                title="Default Social Login Configuration"
                size="lg"
            >
                {centralOauthLoading ? (
                    <div className="py-12 text-center text-sm text-slate-500">Loading default configuration...</div>
                ) : (
                    <div className="space-y-6">
                        <p className="text-sm text-slate-500">
                            Configure platform-wide default social login credentials. Applications without their own
                            configuration will automatically use these defaults.
                        </p>

                        {centralOauthError && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                {centralOauthError}
                            </div>
                        )}
                        {centralOauthSuccess && (
                            <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                                {centralOauthSuccess}
                            </div>
                        )}

                        {/* ── Google (Default) ──────────────────────────── */}
                        <OAuthProviderSection
                            provider="google"
                            label="Google"
                            form={centralOauthForms.google}
                            existingConfig={centralOauthConfigs.find((c) => c.provider === 'google')}
                            isSaving={centralOauthSaving === 'google'}
                            showPrivateKey={centralShowPrivateKey}
                            onTogglePrivateKey={(p) => setCentralShowPrivateKey((prev) => ({ ...prev, [p]: !prev[p] }))}
                            onChange={(field, value) => handleCentralOAuthFormChange('google', field, value)}
                            onSave={() => handleSaveCentralOAuthProvider('google')}
                            onDelete={() => handleDeleteCentralOAuthProvider('google')}
                        />

                        <div className="border-t border-slate-200" />

                        {/* ── Apple (Default) ───────────────────────────── */}
                        <OAuthProviderSection
                            provider="apple"
                            label="Apple"
                            form={centralOauthForms.apple}
                            existingConfig={centralOauthConfigs.find((c) => c.provider === 'apple')}
                            isSaving={centralOauthSaving === 'apple'}
                            showPrivateKey={centralShowPrivateKey}
                            onTogglePrivateKey={(p) => setCentralShowPrivateKey((prev) => ({ ...prev, [p]: !prev[p] }))}
                            onChange={(field, value) => handleCentralOAuthFormChange('apple', field, value)}
                            onSave={() => handleSaveCentralOAuthProvider('apple')}
                            onDelete={() => handleDeleteCentralOAuthProvider('apple')}
                        />
                    </div>
                )}
            </Modal>

            {/* Signing Keys Modal (portal-admin only) */}
            <Modal
                isOpen={showSigningKeysModal}
                onClose={() => {
                    setShowSigningKeysModal(false);
                    setSigningKeysApp(null);
                    setLocalError('');
                }}
                title={`Signing Keys — ${signingKeysApp?.app_name || ''}`}
                size="md"
            >
                {signingKeysLoading ? (
                    <div className="py-12 text-center text-sm text-slate-500">Loading signing keys...</div>
                ) : (
                    <div className="space-y-4">
                        <p className="text-sm text-slate-500">
                            Per-app JWT signing keys override the platform core keys. Rotating or removing a key
                            invalidates active sessions for this app. Key values are never displayed or returned by the
                            API — only whether a custom key is set.
                        </p>

                        {localError && (
                            <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">
                                {localError}
                            </div>
                        )}

                        <div className="space-y-2">
                            {SIGNING_KEY_TYPES.map(({ type, label }) => {
                                const hasCustomKey = !!(signingKeysApp && signingKeys[signingKeysApp.app_id]?.[type]);
                                const isBusy = signingKeysActionType === type;
                                return (
                                    <div
                                        key={type}
                                        className="flex items-center justify-between p-3 rounded-lg border border-slate-200"
                                    >
                                        <div>
                                            <div className="text-sm font-medium text-slate-700">{label}</div>
                                            <span
                                                className={`inline-block mt-1 text-xs font-medium px-2 py-0.5 rounded-full ${
                                                    hasCustomKey
                                                        ? 'bg-emerald-100 text-emerald-700'
                                                        : 'bg-slate-100 text-slate-500'
                                                }`}
                                            >
                                                {hasCustomKey ? 'Custom key' : 'Core fallback'}
                                            </span>
                                        </div>
                                        <div className="flex gap-2">
                                            {hasCustomKey ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRotateSigningKey(type)}
                                                        disabled={isBusy}
                                                        className="px-2.5 py-1.5 rounded-md text-xs font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 hover:bg-indigo-100 disabled:opacity-50"
                                                    >
                                                        {isBusy ? '...' : 'Rotate'}
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleRemoveSigningKey(type)}
                                                        disabled={isBusy}
                                                        className="px-2.5 py-1.5 rounded-md text-xs font-medium text-red-700 bg-red-50 border border-red-100 hover:bg-red-100 disabled:opacity-50"
                                                    >
                                                        {isBusy ? '...' : 'Remove'}
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    onClick={() => handleGenerateSigningKey(type)}
                                                    disabled={isBusy}
                                                    className="px-2.5 py-1.5 rounded-md text-xs font-medium text-primary-700 bg-primary-50 border border-primary-100 hover:bg-primary-100 disabled:opacity-50"
                                                >
                                                    {isBusy ? '...' : 'Generate'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

// ─── OAuthProviderSection ────────────────────────────────────────────────────

/**
 * Renders the configuration form for a single OAuth provider (Google or Apple).
 * Handles toggle, credential fields, save, and delete.
 */
const OAuthProviderSection = ({
    provider,
    label,
    form,
    existingConfig,
    centralConfig,
    isSaving,
    showPrivateKey,
    onTogglePrivateKey,
    onChange,
    onSave,
    onDelete,
}) => {
    const isConfigured = !!existingConfig;
    const isEnabled = form.is_enabled === 1;
    const isApple = provider === 'apple';
    // Show fallback indicator when app has no config but central config exists
    const isUsingCentralFallback = !isConfigured && centralConfig && centralConfig.is_enabled;

    return (
        <div className="space-y-3">
            {/* Header row */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-slate-800">{label} Sign-In</span>
                    {isConfigured && (
                        <span
                            className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                                existingConfig.is_enabled
                                    ? 'bg-emerald-100 text-emerald-700'
                                    : 'bg-slate-100 text-slate-500'
                            }`}
                        >
                            {existingConfig.is_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                    )}
                    {isUsingCentralFallback && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                            Using Default
                        </span>
                    )}
                </div>
                {isConfigured && (
                    <button
                        type="button"
                        onClick={onDelete}
                        disabled={isSaving}
                        className="text-xs text-red-600 hover:text-red-700 disabled:opacity-50"
                    >
                        Remove
                    </button>
                )}
            </div>

            {/* Central fallback info banner */}
            {isUsingCentralFallback && (
                <div className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
                    This app is using the platform default {label} configuration (Client ID:{' '}
                    <span className="font-mono">{centralConfig.client_id}</span>). To override, configure app-specific
                    credentials below.
                </div>
            )}

            {/* Enable / Disable toggle */}
            <label className="flex items-center gap-2 cursor-pointer w-fit">
                <input
                    type="checkbox"
                    checked={isEnabled}
                    onChange={(e) => onChange('is_enabled', e.target.checked ? 1 : 0)}
                    className="rounded border-slate-300 text-primary-600 focus:ring-primary-500"
                />
                <span className="text-sm text-slate-700">Enable {label} login for this app</span>
            </label>

            {/* Credential fields */}
            <div className="space-y-3 pl-1">
                {/* Google: only Client ID */}
                {!isApple && (
                    <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">
                            Google Client ID <span className="text-slate-400">(public — safe for frontend)</span>
                        </label>
                        <input
                            type="text"
                            value={form.client_id}
                            onChange={(e) => onChange('client_id', e.target.value)}
                            placeholder="123456789-abc.apps.googleusercontent.com"
                            className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                        />
                    </div>
                )}

                {/* Apple: Service ID, Team ID, Key ID, Private Key */}
                {isApple && (
                    <>
                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                            <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">
                                    Service ID <span className="text-slate-400">(public)</span>
                                </label>
                                <input
                                    type="text"
                                    value={form.client_id}
                                    onChange={(e) => onChange('client_id', e.target.value)}
                                    placeholder="com.yourcompany.app"
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Team ID</label>
                                <input
                                    type="text"
                                    value={form.team_id}
                                    onChange={(e) => onChange('team_id', e.target.value)}
                                    placeholder="XXXXXXXXXX"
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-slate-600 mb-1">Key ID</label>
                                <input
                                    type="text"
                                    value={form.key_id}
                                    onChange={(e) => onChange('key_id', e.target.value)}
                                    placeholder="XXXXXXXXXX"
                                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
                                />
                            </div>
                        </div>
                        <div>
                            <div className="flex items-center justify-between mb-1">
                                <label className="block text-xs font-medium text-slate-600">
                                    Private Key (p8){' '}
                                    {existingConfig?.has_private_key && (
                                        <span className="text-emerald-600 font-normal">
                                            (saved — leave blank to keep)
                                        </span>
                                    )}
                                </label>
                                <button
                                    type="button"
                                    onClick={() => onTogglePrivateKey(provider)}
                                    className="text-xs text-slate-500 hover:text-slate-700 flex items-center gap-1"
                                >
                                    {showPrivateKey[provider] ? (
                                        <EyeOff className="h-3 w-3" />
                                    ) : (
                                        <Eye className="h-3 w-3" />
                                    )}
                                    {showPrivateKey[provider] ? 'Hide' : 'Show'}
                                </button>
                            </div>
                            <textarea
                                rows={4}
                                value={form.private_key}
                                onChange={(e) => onChange('private_key', e.target.value)}
                                placeholder={'-----BEGIN PRIVATE KEY-----\n...\n-----END PRIVATE KEY-----'}
                                className={`w-full rounded-lg border border-slate-200 px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-primary-200 ${
                                    !showPrivateKey[provider] ? 'text-transparent selection:text-transparent' : ''
                                }`}
                                style={!showPrivateKey[provider] ? { WebkitTextSecurity: 'disc' } : {}}
                            />
                            <p className="text-xs text-slate-400 mt-1">
                                Paste the contents of your AuthKey_XXXXXXXXXX.p8 file. Stored server-side only.
                            </p>
                        </div>
                    </>
                )}
            </div>

            {/* Save button */}
            <div className="flex justify-end">
                <button
                    type="button"
                    onClick={onSave}
                    disabled={isSaving || !form.changed}
                    className="px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                >
                    {isSaving ? 'Saving...' : `Save ${label} Config`}
                </button>
            </div>
        </div>
    );
};

export default AppsPage;
